import React, { useEffect, useMemo, useState } from 'react';
import KaTeXContent from './KaTeXContent';
import { parseQuestionNumber, getResultType, renderModalKatex } from './analysisModalUtils';

/**
 * Shared "Detailed Learning Analysis" (Full Analysis) dialog. Renders score,
 * concept mastery, learning analytics, question-by-question analysis and
 * recommendations for a completed evaluation. Extracted from the coaching task
 * card so it can be reused (e.g. in the View Syllabus modal).
 *
 * Props:
 *  - analysis: the evaluation object (question_analysis, concept_analysis, ...)
 *  - onClose(): close the dialog
 *  - onExplainConcept(question): optional — enables the per-question "ℹ️" action
 *  - onEdit(analysis): optional — enables the default footer "Edit" action
 *  - onPersonalize(analysis): optional — enables "Personalized Sheet"
 *  - subtitle: optional header subtitle text
 *  - insightsLabel: optional label for the insights block (default "Teacher Insights")
 *  - topSlot: optional node rendered under the score card (e.g. a score chart)
 *  - footer: optional node that fully replaces the default footer
 */
const FullAnalysisModal = ({
  analysis,
  onClose,
  onExplainConcept,
  onEdit,
  onPersonalize,
  subtitle = 'Edsupp insights about student performance',
  insightsLabel = 'Teacher Insights',
  topSlot = null,
  footer = null,
}) => {
  const [resultFilter, setResultFilter] = useState('all');

  useEffect(() => {
    if (analysis) renderModalKatex();
  }, [analysis]);

  const questionData = useMemo(() => {
    const source = analysis?.question_analysis || [];
    return [...source]
      .sort((a, b) => parseQuestionNumber(a?.question_number, 0) - parseQuestionNumber(b?.question_number, 0))
      .filter((item) => resultFilter === 'all' || getResultType(item) === resultFilter);
  }, [analysis, resultFilter]);

  if (!analysis) return null;

  const showDefaultFooter = onEdit || onPersonalize;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg w-full max-w-6xl max-h-[95vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="sticky top-0 z-10 p-4 sm:p-6 bg-gradient-to-r from-blue-700 via-indigo-700 to-purple-700 border-b border-gray-200 shadow">
          <div className="flex justify-between items-center">
            <div>
              <h2 className="text-xl font-semibold text-white">Detailed Learning Analysis</h2>
              {subtitle && <p className="text-xs text-white/80 mt-1">{subtitle}</p>}
            </div>
            <button
              onClick={onClose}
              aria-label="Close"
              className="text-white/80 hover:text-white text-2xl sm:text-3xl font-bold w-10 h-10 sm:w-12 sm:h-12 flex items-center justify-center rounded-full hover:bg-white/15 transition-all"
            >
              <svg className="w-5 h-5 sm:w-6 sm:h-6" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2.5} aria-hidden="true"><path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" /></svg>
            </button>
          </div>
        </div>

        {/* Scrollable Content */}
        <div className="flex-1 overflow-y-auto p-4 sm:p-6">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            {/* Left Column - Performance */}
            <div className="space-y-6">
              {/* Score Card */}
              <div className="bg-gradient-to-br from-blue-50 to-indigo-100 p-4 sm:p-6 rounded-lg">
                <div className="text-center">
                  <div className="text-4xl font-bold text-indigo-600 mb-2">
                    {analysis.overall_score}%
                  </div>
                  <div className="text-xl font-semibold text-gray-700 mb-2">
                    Grade: {analysis.grade}
                  </div>
                  <div className="flex justify-center items-center space-x-2">
                    <span className="text-2xl">
                      {analysis.overall_score >= 90 ? '🌟' :
                       analysis.overall_score >= 80 ? '🎉' :
                       analysis.overall_score >= 70 ? '👍' : '💪'}
                    </span>
                  </div>
                </div>
              </div>

              {topSlot}

              {/* Concept Mastery Analysis */}
              {analysis.concept_analysis && (
                <div className="bg-indigo-50 border border-indigo-200 rounded-lg p-4">
                  <h4 className="font-semibold text-indigo-800 mb-3 flex items-center">
                    <span className="mr-2">🎯</span>
                    Concept Mastery Analysis
                  </h4>
                  <div className="space-y-3 max-h-72 overflow-y-auto">
                    {/* Mastered Concepts */}
                    {analysis.concept_analysis.mastered && analysis.concept_analysis.mastered.length > 0 && (
                      <div>
                        <p className="text-xs font-semibold text-green-700 mb-2">🏆 Mastered (≥90%)</p>
                        {analysis.concept_analysis.mastered.map((item, idx) => (
                          <div key={idx} className="flex items-center justify-between text-xs bg-green-100 px-3 py-2 rounded mb-1">
                            <span className="font-medium text-green-900">{item.concept}</span>
                            <div className="flex items-center space-x-2">
                              <span className="text-green-700 font-semibold">{Math.round(item.score * 100)}%</span>
                              <span className={`px-2 py-0.5 rounded text-xs ${
                                item.confidence === 'High' ? 'bg-green-200 text-green-800' :
                                item.confidence === 'Medium' ? 'bg-yellow-200 text-yellow-800' :
                                'bg-gray-200 text-gray-700'
                              }`}>
                                {item.confidence}
                              </span>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}

                    {/* Strong Concepts */}
                    {analysis.concept_analysis.strong && analysis.concept_analysis.strong.length > 0 && (
                      <div>
                        <p className="text-xs font-semibold text-blue-700 mb-2">💪 Strong (75-89%)</p>
                        {analysis.concept_analysis.strong.map((item, idx) => (
                          <div key={idx} className="flex items-center justify-between text-xs bg-blue-100 px-3 py-2 rounded mb-1">
                            <span className="font-medium text-blue-900">{item.concept}</span>
                            <div className="flex items-center space-x-2">
                              <span className="text-blue-700 font-semibold">{Math.round(item.score * 100)}%</span>
                              <span className={`px-2 py-0.5 rounded text-xs ${
                                item.confidence === 'High' ? 'bg-blue-200 text-blue-800' :
                                item.confidence === 'Medium' ? 'bg-yellow-200 text-yellow-800' :
                                'bg-gray-200 text-gray-700'
                              }`}>
                                {item.confidence}
                              </span>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}

                    {/* Developing Concepts */}
                    {analysis.concept_analysis.developing && analysis.concept_analysis.developing.length > 0 && (
                      <div>
                        <p className="text-xs font-semibold text-yellow-700 mb-2">📚 Developing (50-74%)</p>
                        {analysis.concept_analysis.developing.map((item, idx) => (
                          <div key={idx} className="flex items-center justify-between text-xs bg-yellow-100 px-3 py-2 rounded mb-1">
                            <span className="font-medium text-yellow-900">{item.concept}</span>
                            <div className="flex items-center space-x-2">
                              <span className="text-yellow-700 font-semibold">{Math.round(item.score * 100)}%</span>
                              <span className={`px-2 py-0.5 rounded text-xs ${
                                item.confidence === 'High' ? 'bg-yellow-200 text-yellow-800' :
                                item.confidence === 'Medium' ? 'bg-yellow-300 text-yellow-900' :
                                'bg-gray-200 text-gray-700'
                              }`}>
                                {item.confidence}
                              </span>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}

                    {/* At Risk Concepts */}
                    {analysis.concept_analysis.at_risk && analysis.concept_analysis.at_risk.length > 0 && (
                      <div>
                        <p className="text-xs font-semibold text-orange-700 mb-2">⚠️ At Risk (35-49%)</p>
                        {analysis.concept_analysis.at_risk.map((item, idx) => (
                          <div key={idx} className="flex items-center justify-between text-xs bg-orange-100 px-3 py-2 rounded mb-1">
                            <span className="font-medium text-orange-900">{item.concept}</span>
                            <div className="flex items-center space-x-2">
                              <span className="text-orange-700 font-semibold">{Math.round(item.score * 100)}%</span>
                              <span className={`px-2 py-0.5 rounded text-xs ${
                                item.confidence === 'High' ? 'bg-orange-200 text-orange-800' :
                                item.confidence === 'Medium' ? 'bg-yellow-300 text-yellow-900' :
                                'bg-gray-200 text-gray-700'
                              }`}>
                                {item.confidence}
                              </span>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}

                    {/* Weak Concepts */}
                    {analysis.concept_analysis.weak && analysis.concept_analysis.weak.length > 0 && (
                      <div>
                        <p className="text-xs font-semibold text-red-700 mb-2">🚨 Needs Attention (&lt;35%)</p>
                        {analysis.concept_analysis.weak.map((item, idx) => (
                          <div key={idx} className="flex items-center justify-between text-xs bg-red-100 px-3 py-2 rounded mb-1">
                            <span className="font-medium text-red-900">{item.concept}</span>
                            <div className="flex items-center space-x-2">
                              <span className="text-red-700 font-semibold">{Math.round(item.score * 100)}%</span>
                              <span className={`px-2 py-0.5 rounded text-xs ${
                                item.confidence === 'High' ? 'bg-red-200 text-red-800' :
                                item.confidence === 'Medium' ? 'bg-yellow-300 text-yellow-900' :
                                'bg-gray-200 text-gray-700'
                              }`}>
                                {item.confidence}
                              </span>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}

                    {/* No concept data available */}
                    {(!analysis.concept_analysis.mastered || analysis.concept_analysis.mastered.length === 0) &&
                     (!analysis.concept_analysis.strong || analysis.concept_analysis.strong.length === 0) &&
                     (!analysis.concept_analysis.developing || analysis.concept_analysis.developing.length === 0) &&
                     (!analysis.concept_analysis.at_risk || analysis.concept_analysis.at_risk.length === 0) &&
                     (!analysis.concept_analysis.weak || analysis.concept_analysis.weak.length === 0) && (
                      <p className="text-xs text-gray-500 italic">Concept analysis not available for this evaluation.</p>
                    )}
                  </div>
                </div>
              )}

              {/* Learning Analytics */}
              {analysis.learning_analytics && (
                <div className="bg-white border border-gray-200 rounded-lg p-4">
                  <h4 className="font-semibold text-gray-900 mb-4">Learning Analytics</h4>
                  <div className="space-y-3">
                    <div className="flex justify-between items-center">
                      <span className="text-sm text-gray-600">Confidence Level</span>
                      <div className="flex items-center space-x-2">
                        <div className="w-16 bg-gray-200 rounded-full h-2">
                          <div
                            className="bg-blue-600 h-2 rounded-full"
                            style={{ width: `${analysis.learning_analytics.confidence_level * 100}%` }}
                          ></div>
                        </div>
                        <span className="text-sm font-medium">{Math.round(analysis.learning_analytics.confidence_level * 100)}%</span>
                      </div>
                    </div>
                    <div className="flex justify-between items-center">
                      <span className="text-sm text-gray-600">Engagement</span>
                      <div className="flex items-center space-x-2">
                        <div className="w-16 bg-gray-200 rounded-full h-2">
                          <div
                            className="bg-green-600 h-2 rounded-full"
                            style={{ width: `${analysis.learning_analytics.engagement_score * 100}%` }}
                          ></div>
                        </div>
                        <span className="text-sm font-medium">{Math.round(analysis.learning_analytics.engagement_score * 100)}%</span>
                      </div>
                    </div>
                    <div className="pt-2 border-t border-gray-100">
                      <div className="flex justify-between text-sm">
                        <span className="text-gray-600">Learning Pace:</span>
                        <span className="font-medium capitalize">{analysis.learning_analytics.learning_pace}</span>
                      </div>
                      <div className="flex justify-between text-sm mt-1">
                        <span className="text-gray-600">Emotional State:</span>
                        <span className="font-medium capitalize">{analysis.learning_analytics.emotional_state_prediction}</span>
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </div>

            {/* Middle Column - Detailed Analysis */}
            <div className="space-y-6">
              {/* Question-by-Question Analysis */}
              {analysis.question_analysis && analysis.question_analysis.length > 0 && (
                <div className="bg-white border border-gray-200 rounded-lg p-4">
                  <div className="mb-4 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                    <h4 className="font-semibold text-gray-900">Question Analysis</h4>
                    <div className="flex items-center gap-2">
                      <label htmlFor="full-analysis-result-filter" className="text-xs sm:text-sm text-gray-700">Show:</label>
                      <select
                        id="full-analysis-result-filter"
                        value={resultFilter}
                        onChange={(e) => setResultFilter(e.target.value)}
                        className="text-xs sm:text-sm border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-indigo-500"
                      >
                        <option value="all">All Answers</option>
                        <option value="correct">Correct Only</option>
                        <option value="partial">Partially Correct Only</option>
                        <option value="incorrect">Incorrect Only</option>
                      </select>
                    </div>
                  </div>
                  <div className="space-y-3 max-h-64 overflow-y-auto">
                    {questionData.map((qa, index) => (
                      <div key={index} className="p-3 bg-gray-50 rounded-lg">
                        <div className="flex items-center justify-between mb-2">
                          <div className="flex items-center space-x-2">
                            {onExplainConcept && (
                              <button
                                type="button"
                                onClick={() => onExplainConcept(qa)}
                                className="text-xs px-2 py-1 bg-yellow-100 text-blue-700 rounded hover:bg-yellow-200"
                                title="Explain this concept"
                              >
                                ℹ️
                              </button>
                            )}
                            <span className="text-sm font-medium text-gray-700">
                              Q{parseQuestionNumber(qa.question_number, index)}:{' '}
                              <KaTeXContent content={qa.question_text || 'Question not available'} className="inline" />
                            </span>
                          </div>
                          <span className={`px-2 py-1 text-xs font-bold rounded ${
                            qa.is_correct ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
                          }`}>
                            {qa.is_correct ? 'Correct' : `${Math.round(qa.partial_credit * 100)}% Credit`}
                          </span>
                        </div>
                        <div className="text-xs text-gray-600 space-y-1">
                          <p>
                            <strong>Student:</strong>{' '}
                            <KaTeXContent content={qa.student_answer || 'No answer provided'} className="inline" />
                            {qa.hint_used && (
                              <span className="ml-2 text-xs bg-yellow-100 text-yellow-800 px-1 py-0.5 rounded">
                                💡 Hint used
                              </span>
                            )}
                          </p>
                          <p><strong>Expected:</strong>{' '}<KaTeXContent content={qa.correct_answer || 'Answer not available'} className="inline" /></p>
                          <p><strong>Analysis:</strong>{' '}<KaTeXContent content={qa.feedback || 'Feedback not available'} className="inline" /></p>
                          {qa.answer_explanation && (
                            <div className="text-gray-700">
                              <strong>Explanation:</strong>{' '}
                              <KaTeXContent content={qa.answer_explanation} className="inline" />
                            </div>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Insights (labelled per audience — Teacher/Parent) */}
              {analysis.parent_insights && (
                <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
                  <h4 className="font-semibold text-amber-800 mb-3 flex items-center">
                    <span className="mr-2">💡</span>
                    {insightsLabel}
                  </h4>
                  <p className="text-amber-700 text-sm leading-relaxed">{analysis.parent_insights}</p>
                </div>
              )}
            </div>

            {/* Right Column - Recommendations */}
            <div className="space-y-6">
              {/* Strengths */}
              <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                <h4 className="font-semibold text-green-800 mb-3 flex items-center">
                  <span className="mr-2">🌟</span>
                  Strengths
                </h4>
                <p className="text-green-700 text-sm leading-relaxed">{analysis.strengths || 'Good understanding of core concepts.'}</p>
              </div>

              {/* Areas for Improvement */}
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                <h4 className="font-semibold text-blue-800 mb-3 flex items-center">
                  <span className="mr-2">📈</span>
                  Growth Areas
                </h4>
                <p className="text-blue-700 text-sm leading-relaxed">{analysis.areas_for_improvement || 'Continue practicing to strengthen understanding.'}</p>
              </div>

              {/* Learning Suggestions */}
              {analysis.learning_suggestions && (
                <div className="bg-purple-50 border border-purple-200 rounded-lg p-4">
                  <h4 className="font-semibold text-purple-800 mb-3 flex items-center">
                    <span className="mr-2">🚀</span>
                    Next Steps
                  </h4>
                  <p className="text-purple-700 text-sm leading-relaxed mb-3">{analysis.learning_suggestions}</p>

                  {analysis.learning_analytics?.recommended_activities && (
                    <div>
                      <p className="text-xs font-medium text-purple-800 mb-2">Recommended Activities:</p>
                      <ul className="text-xs text-purple-700 space-y-1">
                        {analysis.learning_analytics.recommended_activities.map((activity, index) => (
                          <li key={index} className="flex items-start">
                            <span className="mr-2">•</span>
                            {activity}
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}
                </div>
              )}

              {/* Teacher Communication */}
              {analysis.learning_analytics?.parent_communication && (
                <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
                  <h4 className="font-semibold text-gray-800 mb-3 flex items-center">
                    <span className="mr-2">💬</span>
                    Discussion Points
                  </h4>
                  <p className="text-gray-700 text-sm leading-relaxed">{analysis.learning_analytics.parent_communication}</p>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Footer — a custom `footer` slot fully replaces the default actions */}
        {footer ? (
          footer
        ) : showDefaultFooter && (
          <div className="p-4 sm:p-6 border-t border-gray-200 bg-gray-50">
            <div className="flex gap-3 justify-end">
              {onEdit && (
                <button
                  onClick={() => onEdit(analysis)}
                  className="px-4 py-2 border-2 bg-orange-600 text-white border-orange-800 hover:bg-orange-70 rounded-lg font-medium flex items-center text-sm"
                >
                  ✏️ Edit
                </button>
              )}
              {onPersonalize && (
                <button
                  onClick={() => onPersonalize(analysis)}
                  className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 font-medium flex items-center text-sm"
                >
                  🎯 Personalized Sheet
                </button>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default FullAnalysisModal;
